import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { bulkDeleteAdminUsers, deleteAdminUser, fetchAdminUsers, type ActorRole } from "../lib/api";
import { Button } from "./ui/button";

type AdminUsersPanelProps = {
  actorRole: ActorRole;
};

const adminUserRoleLabels: Record<string, string> = {
  advertiser_owner: "Advertiser owner",
  platform_admin: "Platform admin",
  platform_support: "Support operator",
  webmaster: "Webmaster"
};

export function AdminUsersPanel({ actorRole }: AdminUsersPanelProps) {
  const { t } = useTranslation(["admin", "common"]);
  const queryClient = useQueryClient();
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const usersQuery = useQuery({
    queryKey: ["admin-users", actorRole],
    queryFn: fetchAdminUsers
  });

  const canDelete = usersQuery.data?.can_delete ?? actorRole === "platform_admin";
  const users = usersQuery.data?.items ?? [];
  const allSelected = users.length > 0 && selectedIDs.length === users.length;

  const deleteMutation = useMutation({
    mutationFn: (userID: string) => deleteAdminUser(userID),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSelectedIDs((current) => current.filter((id) => users.some((user) => user.id === id)));
    }
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: ({ ids, phrase }: { ids: string[]; phrase: string }) => bulkDeleteAdminUsers(ids, phrase),
    onSuccess: async () => {
      setSelectedIDs([]);
      setConfirmPhrase("");
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    }
  });

  const selectedCount = selectedIDs.length;
  const canConfirmBulkDelete = canDelete && selectedCount > 0 && confirmPhrase === "DELETE" && !bulkDeleteMutation.isPending;
  const selectedSet = useMemo(() => new Set(selectedIDs), [selectedIDs]);

  return (
    <section className="data-panel" aria-label={t("admin:users.title")}>
      <div className="panel-title">
        <h2>{t("admin:users.title")}</h2>
        <div className="panel-actions">
          <input
            aria-label={t("admin:users.confirmLabel")}
            className="confirm-input"
            data-testid="bulk-delete-confirm-input"
            disabled={!canDelete}
            onChange={(event) => setConfirmPhrase(event.target.value)}
            placeholder={t("admin:users.confirmPlaceholder")}
            value={confirmPhrase}
          />
          <Button
            aria-label={t("admin:users.bulkDelete")}
            data-testid="bulk-delete-button"
            disabled={!canConfirmBulkDelete}
            icon={<Trash2 aria-hidden="true" size={16} />}
            onClick={() => bulkDeleteMutation.mutate({ ids: selectedIDs, phrase: confirmPhrase })}
            variant="danger"
          >
            {t("admin:users.bulkDelete")} ({selectedCount})
          </Button>
        </div>
      </div>
      {!canDelete ? <p className="panel-note">{t("admin:users.rbacNotice")}</p> : null}
      <div className="data-table admin-users-table" role="table">
        <div className="data-row admin-users-row data-head" role="row">
          <span role="columnheader">
            <input
              aria-label={t("admin:users.selectAll")}
              checked={allSelected}
              disabled={users.length === 0}
              onChange={() => {
                if (allSelected) {
                  setSelectedIDs([]);
                  return;
                }
                setSelectedIDs(users.map((user) => user.id));
              }}
              type="checkbox"
            />
          </span>
          <span role="columnheader">{t("admin:users.email")}</span>
          <span role="columnheader">{t("admin:users.role")}</span>
          <span role="columnheader">{t("admin:users.actions")}</span>
        </div>
        {users.map((user) => {
          const selected = selectedSet.has(user.id);
          return (
            <div className="data-row admin-users-row" key={user.id} role="row">
              <span role="cell">
                <input
                  aria-label={user.email}
                  data-testid={`admin-user-select-${user.id}`}
                  checked={selected}
                  onChange={() => {
                    setSelectedIDs((current) => {
                      if (selected) {
                        return current.filter((id) => id !== user.id);
                      }
                      return [...current, user.id];
                    });
                  }}
                  type="checkbox"
                />
              </span>
              <span role="cell">{user.email}</span>
              <span role="cell">{adminUserRoleLabels[user.role] ?? user.role}</span>
              <span role="cell">
                <Button
                  aria-label={`${t("admin:users.delete")} ${user.email}`}
                  data-testid={`admin-user-delete-${user.id}`}
                  disabled={!canDelete || deleteMutation.isPending}
                  icon={<Trash2 aria-hidden="true" size={16} />}
                  onClick={() => deleteMutation.mutate(user.id)}
                  size="sm"
                  variant="danger"
                >
                  {t("admin:users.delete")}
                </Button>
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
